import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBadgeNumbers } from "@/features/statistics/services/statistics-service";

/**
 * Custom hook to manage and provide counts for different entities
 * Uses a single optimized API endpoint for better performance
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Numbers data with loading states and error handling
 */
export const useNumbers = (options = {}) => {
  const { enabled = true } = options;

  // Single query to fetch all badge numbers
  const {
    data: badgeData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["badge-numbers"],
    queryFn: fetchBadgeNumbers,
    enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
    refetchOnWindowFocus: true,
  });

  // Memoize the computed numbers to avoid unnecessary re-renders
  const numbers = useMemo(
    () => ({
      packages: badgeData?.packages || 0,
      pickups: badgeData?.pickups || 0,
      invoices: badgeData?.invoices || 0,
      tickets: badgeData?.tickets || 0,
      returnVouchers: badgeData?.returnVouchers || 0,
      users: badgeData?.users || 0,
      unassignedPackages: badgeData?.unassignedPackages || 0,
      unassignedPickups: badgeData?.unassignedPickups || 0,
    }),
    [badgeData]
  );

  // Overall loading state
  const hasError = Boolean(error);

  return {
    numbers,
    isLoading,
    hasError,
    error,
    // Utility functions
    isEmpty:
      numbers.packages === 0 &&
      numbers.pickups === 0 &&
      numbers.invoices === 0 &&
      numbers.tickets === 0 &&
      numbers.returnVouchers === 0 &&
      numbers.users === 0 &&
      numbers.unassignedPackages === 0 &&
      numbers.unassignedPickups === 0,
    hasAnyNumbers:
      numbers.packages > 0 ||
      numbers.pickups > 0 ||
      numbers.invoices > 0 ||
      numbers.tickets > 0 ||
      numbers.returnVouchers > 0 ||
      numbers.users > 0 ||
      numbers.unassignedPackages > 0 ||
      numbers.unassignedPickups > 0,
  };
};

export default useNumbers;
